import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, qs } from '../dom.js';
import { icon } from '../icons.js';

export async function reviewView(params: Record<string, string>): Promise<RenderedView> {
  const assignment = await repos.getAssignment(params.id);
  if (!assignment) return { title: 'Review', back: '/quizzes', html: `<div class="view"><p class="empty">Assignment not found.</p></div>` };
  const quiz = (await repos.getQuiz(assignment.quizId))!;
  const [results, students, cls] = await Promise.all([
    repos.resultsForAssignment(assignment.id),
    repos.studentsInClass(assignment.classId),
    repos.getClass(assignment.classId),
  ]);
  const nameOf = new Map(students.map((s) => [s.id, s.name]));

  results.sort((a, b) => {
    const an = (a.studentId && nameOf.get(a.studentId)) || '~';
    const bn = (b.studentId && nameOf.get(b.studentId)) || '~';
    return an.localeCompare(bn) || b.capturedAt - a.capturedAt;
  });

  const scored = results.filter((r) => r.score != null && r.maxScore);
  const mps = scored.length ? Math.round((100 * scored.reduce((s, r) => s + (r.score ?? 0), 0)) / (scored.length * (scored[0].maxScore || 1))) : null;

  const rows = results
    .map((r) => {
      const who = r.studentId
        ? nameOf.get(r.studentId) ?? 'Unknown'
        : r.capturedName
          ? r.capturedName
          : `Unassigned (${esc(r.idDigitsRead || '—')})`;
      const score = r.score != null ? `${r.score}/${r.maxScore}` : 'unscored';
      const badge = r.needsReview ? `<span class="badge badge-warn">${icon.alert()} review</span>` : '';
      // Unidentified paper with a captured name-box crop → show it as a thumbnail
      // so the teacher can recognise the student without opening the result.
      const thumb = !r.studentId && r.capturedNameImage ? `<img class="name-thumb" id="thumb-${esc(r.id)}" alt="scanned name">` : '';
      return `<button class="row" data-result="${esc(r.id)}">${thumb}<span class="grow"><span class="ttl">${esc(who)}</span><span class="meta">${esc(score)}</span></span>${badge}<span class="chev">${icon.chevron()}</span></button>`;
    })
    .join('');

  return {
    title: 'Review papers',
    subtitle: `${quiz.title} · ${cls?.name ?? ''}`,
    back: `/quiz/${quiz.id}`,
    action: { label: 'Scan', icon: icon.camera(), href: `/assignment/${assignment.id}/scan` },
    html: `<div class="view">
      ${results.length ? `<div class="card"><strong>${results.length}</strong> scanned${mps != null ? ` · class MPS <strong>${mps}%</strong>` : ''}</div>
        <button class="btn btn-primary btn-block" data-reports style="margin-top:10px">${icon.doc()} Print / Excel reports</button>
        <button class="btn btn-ghost btn-block" data-gaps style="margin-top:10px">${icon.list()} Item/Competency Analysis</button>
        <button class="btn btn-ghost btn-block" data-analysis style="margin:10px 0 14px">${icon.chart()} Item analysis &amp; CSV export</button>
        <ul class="list">${rows}</ul>`
        : `<div class="empty">${icon.list()}<p>No papers scanned yet.</p></div>`}
    </div>`,
    mount(root) {
      // Fill in the name-box thumbnails for unidentified papers.
      for (const r of results) {
        if (r.studentId || !r.capturedNameImage) continue;
        const img = qs<HTMLImageElement>(root, `#thumb-${CSS.escape(r.id)}`);
        if (img) img.src = URL.createObjectURL(r.capturedNameImage);
      }
      onClick(root, '[data-result]', (el) => navigate(`#/result/${el.dataset.result}`));
      onClick(root, '[data-analysis]', () => navigate(`#/assignment/${assignment.id}/analysis`));
      onClick(root, '[data-gaps]', () => navigate(`#/assignment/${assignment.id}/gaps`));
      onClick(root, '[data-reports]', () => navigate(`#/assignment/${assignment.id}/reports`));
    },
  };
}
